import type { components } from '@pba/types-openapi-client';

import { type Entity, createEntity } from './entity';
import { type Image, createImage } from './image';
import { type UploadedFile, createUploadedFile } from './uploaded-file';

export type OrganizationBase = {
  city?: string;
  landscapeLogo?: Image;
  profilePhoto?: Image;
  squareLogo?: Image;
  state?: string;
  whiteProfilePhoto?: Image;
} & OrganizationSlim;

export type OrganizationList = {
  categories: Entity[];
  coverPhoto?: UploadedFile;
  description: string;
  groupId?: null | string;
  isDefault: boolean;
  membersCount: number;
  privacy: OrganizationPrivacy;
  requiredConsents: Array<{ id: string }>;
  settings: OrganizationSettings;
  tags: Array<components['schemas']['Tag']>;
  url: string;
  usersCount: number;
} & OrganizationBase;

export type OrganizationPrivacy = components['schemas']['OrganizationPrivacyEnum'];

export type OrganizationSettings = {
  allowedIntegrations?: string[];
  allowMedicalCommunity?: boolean;
};

export type OrganizationSlim = {
  customChannels: boolean;
  intId: number;
} & Entity;

type OrganizationListDataObject = components['schemas']['OrganizationList'];

type OrganizationSettingsDataObject = OrganizationListDataObject['settings'];

const createOrganizationSettings = (data: OrganizationSettingsDataObject): OrganizationSettings => ({
  allowedIntegrations: data.allowed_integrations,
  allowMedicalCommunity: data.allow_medical_community,
});

export const createOrganizationSlim = (data: components['schemas']['OrganizationSlim']): OrganizationSlim => ({
  customChannels: data.custom_channels,
  id: data.id,
  intId: data.intId,
  name: data.name,
});

export const createOrganizationBase = (data: components['schemas']['OrganizationBase']): OrganizationBase => ({
  ...createOrganizationSlim(data),
  city: data.city,
  landscapeLogo: data.landscape_logo ? createImage(data.landscape_logo) : undefined,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- OpenAPI marks profile_photo as always present, but API responses may omit it
  profilePhoto: data.profile_photo ? createImage(data.profile_photo) : undefined,
  squareLogo: data.square_logo ? createImage(data.square_logo) : undefined,
  state: data.state,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- OpenAPI marks white_profile_photo as always present, but API responses may omit it
  whiteProfilePhoto: data.white_profile_photo ? createImage(data.white_profile_photo) : undefined,
});

export const createOrganizationList = (data: OrganizationListDataObject): OrganizationList => ({
  ...createOrganizationBase(data),
  categories: data.categories.map((category) => createEntity(category)),
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- OpenAPI marks cover_photo as always present, but API responses may omit it
  coverPhoto: data.cover_photo ? createUploadedFile(data.cover_photo) : undefined,
  description: data.description,
  groupId: data.group_id ?? undefined,
  isDefault: data.is_default,
  membersCount: data.members_count,
  privacy: data.privacy,
  requiredConsents: data.required_consents,
  settings: createOrganizationSettings(data.settings),
  tags: data.tags,
  url: data.url,
  usersCount: data.users_count,
});
